/**
 * EntitySearchInput — Spec 49 Phase 2b.
 *
 * Reusable search bar + scoped-select row + ranked results list for
 * Templates, Workflows, and Workforce list pages (entity-management §1.3).
 * Demo page: /tables/data-tables (header search and selects); NextAdmin `last-transactions-table`.
 *
 * Usage:
 *   <EntitySearchInput
 *     entityType="template"
 *     placeholder="Search templates…"
 *     scopedDropdowns={[{ field: 'department', label: 'Department', options: [...], onChange: ... }]}
 *     onSelect={(row) => navigate(`/agents/templates/${row.id}`)}
 *   />
 *
 * When the query is empty the results section is hidden. Scoped selects
 * filter the DISPLAYED results client-side so the server always gets the
 * full query — no extra round-trips for select changes.
 *
 * Source chips:
 *   local       → neutral
 *   builtin     → success
 *   marketplace → warning (opens the marketplace detail sheet for the matching listing)
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { listMarketplaceListingsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { MarketplaceListing } from '../../generated/api/types.gen.js';
import { useEntitySearch, type EntityType, type EntitySearchResult } from '../../hooks/useEntitySearch.js';
import type { WorkforceSearchScope } from '../../services/api/entity-search.api.js';
import { Badge, type BadgeVariant } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { FilterSelect } from '../ui/FilterSelect.js';
import { SearchBar } from '../ui/SearchBar.js';
import { MarketplaceDetailSheet } from './MarketplaceDetailSheet.js';

export interface ScopedDropdown {
  field: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  value?: string;
  onChange: (value: string | undefined) => void;
}

export interface EntitySearchInputProps {
  entityType: EntityType;
  placeholder?: string;
  scopedDropdowns?: ScopedDropdown[];
  onSelect: (row: EntitySearchResult) => void;
  /** When true, marketplace rows are shown in results. Default true. */
  includeMarketplace?: boolean;
  className?: string;
  /** Optional debounce override (ms). Default 250. */
  debounceMs?: number;
  /** Optional result limit. Default 10. */
  limit?: number;
  /** Workforce/daemon route scope. Applies only to entityType='employee'. */
  workforceScope?: WorkforceSearchScope;
}

/** Source chip labels and variants. */
const SOURCE_CHIP: Record<string, { label: string; variant: BadgeVariant }> = {
  local: { label: 'Local', variant: 'neutral' },
  builtin: { label: 'Built-in', variant: 'success' },
  marketplace: { label: 'Marketplace', variant: 'warning' },
};

function sourceChip(source: string) {
  return SOURCE_CHIP[source] ?? SOURCE_CHIP.local;
}

/** The marketplace's entity type for each searchable kind. */
const LISTING_TYPE: Record<EntityType, string> = {
  template: 'agent_template',
  workflow: 'workflow',
  employee: 'daemon',
};

export function EntitySearchInput({
  entityType,
  placeholder = 'Search…',
  scopedDropdowns = [],
  onSelect,
  includeMarketplace = true,
  className = '',
  debounceMs,
  limit,
  workforceScope,
}: EntitySearchInputProps) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [marketplaceListing, setMarketplaceListing] = useState<MarketplaceListing | null>(null);

  const { data: allResults = [], isFetching } = useEntitySearch(entityType, query, {
    debounceMs,
    limit,
    workforceScope,
  });

  // Apply select filters client-side so they don't trigger new API calls.
  const results = allResults.filter((row) => {
    if (!includeMarketplace && row.source === 'marketplace') return false;

    for (const dd of scopedDropdowns) {
      if (dd.value && String(row[dd.field] ?? '') !== dd.value) return false;
    }
    return true;
  });

  const isEmpty = query.trim().length === 0;

  const handleSelect = async (row: EntitySearchResult) => {
    if (row.source !== 'marketplace') {
      onSelect(row);
      return;
    }
    // A marketplace row opens the detail sheet of its real listing; a row with no listing opens like any other.
    const listings = await queryClient.fetchQuery(
      listMarketplaceListingsOptions({ query: { type: LISTING_TYPE[entityType], search: row.name, limit: 20 } }),
    );
    const listing = listings.data.find((candidate) => candidate.entityId === row.id);
    if (listing) setMarketplaceListing(listing);
    else onSelect(row);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Search bar + scoped selects row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <SearchBar value={query} onChange={setQuery} placeholder={placeholder} className="w-full sm:w-full" />
        </div>

        {scopedDropdowns.map((dd) => (
          <FilterSelect
            key={dd.field}
            label={dd.label}
            anyLabel={`All ${dd.label.toLowerCase()}`}
            value={dd.value ?? ''}
            options={dd.options}
            onChange={(next) => dd.onChange(next === '' ? undefined : next)}
          />
        ))}
      </div>

      {/* Results list — hidden when query is empty */}
      {!isEmpty && (
        <div className="divide-y divide-border overflow-hidden rounded-lg border bg-surface">
          {results.length === 0 && !isFetching && (
            <p className="px-4 py-3 text-sm text-faint">No results for &ldquo;{query}&rdquo;.</p>
          )}

          {results.length === 0 && isFetching && <p className="px-4 py-3 text-sm text-faint">Searching…</p>}

          {results.map((row) => {
            const chip = sourceChip(row.source);
            return (
              <Button
                key={`${row.source}-${row.id}`}
                variant="ghost"
                onClick={() => handleSelect(row)}
                className="h-auto w-full justify-between gap-3 rounded-none px-4 py-2.5 text-left"
              >
                {/* Name + department */}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-heading">{row.name}</span>
                  {(row.department || row.category) && (
                    <span className="block truncate text-xs font-normal text-muted">
                      {String(row.department ?? row.category ?? '')}
                    </span>
                  )}
                </span>

                {/* Source chip */}
                <Badge variant={chip.variant}>{chip.label}</Badge>
              </Button>
            );
          })}
        </div>
      )}

      {/* Empty-query hint */}
      {isEmpty && <p className="text-xs text-faint">Type to search…</p>}

      <MarketplaceDetailSheet listing={marketplaceListing} onClose={() => setMarketplaceListing(null)} />
    </div>
  );
}
